import { useMemo, useState } from 'react';
import { ClipboardList, Wand2, AlertTriangle, X, CalendarRange } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ModuleHero } from '../components/ModuleHero';
import { useStore } from '../hooks/useStore';
import { listGames, listScheduleSlots, listRinksForTournament } from '../services/schedulerService';
import { listTeams } from '../services/rosterService';
import {
  listAssignmentsForGame,
  eligibleCrewProfiles,
  assignCrew,
  unassignCrew,
  autoAssignCrew,
  detectCrewConflicts,
  type CrewPosition,
} from '../services/operationsService';
import { listProfiles } from '../services/directoryService';
import { useActiveTournamentMaybe } from '../state/TournamentContext';

export function OperationsPage() {
  const db = useStore((d) => d);
  // When used inside a competition workspace, the active season/tournament is pre-resolved.
  const workspaceTournament = useActiveTournamentMaybe()?.tournament;

  const tournaments = useMemo(
    () => [...db.tournaments].filter((t) => t.status !== 'archived').sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
    [db.tournaments],
  );
  const [tournamentId, setTournamentId] = useState<string>(
    () => workspaceTournament?.id ?? tournaments[0]?.id ?? '',
  );
  const [notice, setNotice] = useState<string | null>(null);

  // If we're in a workspace, always use the workspace tournament.
  const tournament = workspaceTournament ?? tournaments.find((t) => t.id === tournamentId);
  const isInWorkspace = Boolean(workspaceTournament);
  const games = tournament ? listGames(tournament.id) : [];
  const slots = tournament ? listScheduleSlots(tournament.id) : [];
  const slotById = new Map(slots.map((s) => [s.id, s]));
  const rinks = tournament ? listRinksForTournament(tournament.id) : [];
  const teams = tournament ? listTeams(tournament.id) : [];
  const profiles = listProfiles();
  const profileById = new Map(profiles.map((p) => [p.id, p]));
  const conflicts = tournament ? detectCrewConflicts(tournament.id) : [];

  return (
    <>
      <ModuleHero
        eyebrow="Operate"
        title="Game Staff Coverage"
        description="Assign scorekeepers, referees, and game staff coverage. Teams owns who plays; Schedule owns when and where games happen. Conflicts are flagged in real time so nobody is double-booked."
        icon={ClipboardList}
        accent="arc"
      >
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {/* Only show the tournament picker when NOT embedded in a workspace */}
          {!isInWorkspace && (
            <select
              className="select"
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
              style={{ maxWidth: 280 }}
            >
              <option value="">Select a season or tournament...</option>
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          {tournament && games.length > 0 && (
            <button
              className="btn btn--primary"
              onClick={() => {
                const r = autoAssignCrew(tournament.id);
                setNotice(`Auto-assigned ${r.assigned} crew slot${r.assigned === 1 ? '' : 's'}.`);
              }}
            >
              <Wand2 size={14} /> Auto-assign crew
            </button>
          )}
        </div>
      </ModuleHero>

      {!tournament ? (
        <div className="empty">Pick a season or tournament above to get started.</div>
      ) : games.length === 0 ? (
        <div className="note note--warn" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CalendarRange size={16} style={{ flexShrink: 0 }} />
          <div>
            No games yet — generate a schedule in the{' '}
            {isInWorkspace ? (
              <Link to="../schedule" relative="path">Schedule tab</Link>
            ) : (
              'Schedule tab'
            )}{' '}
            first. Once the schedule is generated, come back here to assign crew.
          </div>
        </div>
      ) : (
        <>
          {notice && (
            <div className="note note--ok" role="status">
              {notice}
            </div>
          )}
          {conflicts.length > 0 && (
            <div className="note note--err">
              <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
              {conflicts.length} crew conflict{conflicts.length === 1 ? '' : 's'} detected — see the highlighted rows below.
            </div>
          )}

          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Rink</th>
                  <th>Match</th>
                  <th>Scorekeeper</th>
                  <th>Referee</th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => {
                  const slot = g.scheduleSlotId ? slotById.get(g.scheduleSlotId) : null;
                  const rink = slot ? rinks.find((r) => r.id === slot.rinkId) : null;
                  const home = teams.find((t) => t.id === g.homeTeamId);
                  const away = teams.find((t) => t.id === g.awayTeamId);
                  const assignments = listAssignmentsForGame(g.id);
                  const conflictRow = conflicts.some((c) => c.gameAId === g.id || c.gameBId === g.id);
                  return (
                    <tr key={g.id} style={conflictRow ? { background: 'rgba(255,85,119,0.08)' } : undefined}>
                      <td>{slot ? new Date(slot.startsAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                      <td>{rink?.name ?? '—'}</td>
                      <td><strong>{home?.name}</strong> vs {away?.name}</td>
                      <td>
                        <CrewSlot
                          gameId={g.id}
                          position="scorekeeper"
                          assignments={assignments}
                          profileById={profileById}
                        />
                      </td>
                      <td>
                        <CrewSlot
                          gameId={g.id}
                          position="referee_main"
                          assignments={assignments}
                          profileById={profileById}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function CrewSlot({
  gameId,
  position,
  assignments,
  profileById,
}: {
  gameId: string;
  position: CrewPosition;
  assignments: ReturnType<typeof listAssignmentsForGame>;
  profileById: Map<string, { displayName: string }>;
}) {
  const filled = assignments.filter((a) => a.position === position);
  const eligible = eligibleCrewProfiles(position);

  return (
    <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {filled.map((a) => (
        <span key={a.id} className="chip chip--accent">
          {profileById.get(a.profileId)?.displayName ?? '—'}
          <button
            onClick={() => unassignCrew(a.id)}
            style={{ background: 'none', border: 'none', color: 'inherit', marginLeft: 4, cursor: 'pointer', padding: 0, display: 'inline-flex' }}
            aria-label="Remove"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <select
        className="select"
        style={{ maxWidth: 180, padding: '6px 8px' }}
        value=""
        onChange={(e) => {
          const profileId = e.target.value;
          if (!profileId) return;
          assignCrew({ gameId, profileId, position });
          e.currentTarget.value = '';
        }}
      >
        <option value="">+ Add</option>
        {eligible
          .filter((p) => !filled.some((a) => a.profileId === p.id))
          .map((p) => (
            <option key={p.id} value={p.id}>{p.displayName}</option>
          ))}
      </select>
    </div>
  );
}
